<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useModalStore } from '@/store/modal';
import { useResizeObserver } from '@vueuse/core';
import { useImage } from 'vue-konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import type StageType from 'konva/lib/Stage';
import type {
  BuildingFloor,
  BuildingPlan,
  BuildingRoom,
  BuildingRoomShape,
  PolygonPoint,
} from '@/types/building';
import floorImage1 from '@/assets/images/floor_1.jpg';
import floorImage2 from '@/assets/images/floor_2.jpg';
import floorImage3 from '@/assets/images/floor_3.jpg';

type BuildingModalProps = {
  polygon?: {
    id?: string;
    meta?: Record<string, any>;
  };
  buildingPlan?: BuildingPlan;
  tokensAdded?: number;
};

const ROOM_STATUS_COLORS: Record<
  NonNullable<BuildingRoom['status']> | 'default',
  { bg: string; border: string; fill: string }
> = {
  occupied: {
    bg: 'rgba(59, 130, 246, 0.8)',
    border: 'rgba(59, 130, 246, 0.9)',
    fill: '#2563eb',
  },
  vacant: {
    bg: 'rgba(230, 126, 34, 0.8)',
    border: 'rgba(230, 126, 34, 0.9)',
    fill: '#E67E22',
  },
  reserved: {
    bg: 'rgba(250, 204, 21, 0.28)',
    border: 'rgba(250, 204, 21, 0.8)',
    fill: '#ca8a04',
  },
  changingRoom: {
    bg: 'rgba(231, 76, 60, 0.8)',
    border: 'rgba(231, 76, 60, 0.9)',
    fill: '#e74c3c',
  },
  sanitaryFacilities: {
    bg: 'rgba(241, 196, 15, 0.8)',
    border: 'rgba(241, 196, 15, 0.9)',
    fill: '#f1c40f',
  },
  auxiliaryRooms: {
    bg: 'rgba(46, 204, 113, 0.8)',
    border: 'rgba(46, 204, 113, 0.9)',
    fill: '#2ed573',
  },
  default: {
    bg: 'rgba(148, 163, 184, 0.18)',
    border: 'rgba(148, 163, 184, 0.5)',
    fill: '#475569',
  },
};

const ROOM_STATUS_LABELS: Record<NonNullable<BuildingRoom['status']>, string> = {
  occupied: 'Занято',
  vacant: 'Свободно',
  reserved: 'Резерв',
  changingRoom: 'Раздевалка',
  sanitaryFacilities: 'Санитарный узел',
  auxiliaryRooms: 'Сопутствующие помещения',
};

const fallbackBuildingPlan: BuildingPlan = {
  name: 'Многофункциональный центр',
  address: 'г. Алматы, ул. Абая 10',
  floors: [
    {
      id: 'floor-1',
      name: '1 этаж',
      level: 1,
      note: 'Публичная зона с торговыми помещениями и точками питания.',
      image: floorImage1,
      rooms: [
    {
        "id": "f1-room-1",
        "name": "27",
        "tenant": "ТОО СХК Динамо",
        "status": "occupied",
        "description": "Сушилка",
        "area": 43.4,
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 6.339,
                    "y": 37.152
                },
                {
                    "x": 5.378,
                    "y": 36.146
                },
                {
                    "x": 7.337,
                    "y": 32.05
                },
                {
                    "x": 8.528,
                    "y": 33.918
                },
                {
                    "x": 9.68,
                    "y": 35.858
                },
                {
                    "x": 8.566,
                    "y": 37.511
                }
            ]
        }
    },
    {
        "id": "72769926-8178-401f-81b1-164c56def622",
        "name": "29",
        "status": "occupied",
        "description": "тренерская\nДСС/ХА/ДИНАМО Алматы/тренерская/2025",
        "tenant": "ТОО СХК Динамо",
        "area": 6.1,
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 9.643,
                    "y": 37.531
                },
                {
                    "x": 10.206,
                    "y": 36.478
                },
                {
                    "x": 11.401,
                    "y": 38.539
                },
                {
                    "x": 10.674,
                    "y": 39.768
                }
            ]
        }
    },
    {
        "id": "ef9be35e-1068-4903-aa67-025be4978151",
        "name": "тренерская",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 4.761,
                    "y": 41.979
                },
                {
                    "x": 7.41,
                    "y": 42.33
                },
                {
                    "x": 7.504,
                    "y": 44.786
                },
                {
                    "x": 8.465,
                    "y": 44.874
                },
                {
                    "x": 8.536,
                    "y": 46.804
                },
                {
                    "x": 9.473,
                    "y": 46.804
                },
                {
                    "x": 9.567,
                    "y": 39.435
                },
                {
                    "x": 8.981,
                    "y": 38.602
                },
                {
                    "x": 5.629,
                    "y": 38.777
                }
            ]
        },
        "tenant": "ТОО СХД Динамо",
        "area": 50
    },
    {
        "id": "8a6f6948-6df3-4297-bbe4-28474f3a430d",
        "name": "Новая зона 2",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 12.591,
                    "y": 63.12
                },
                {
                    "x": 13.224,
                    "y": 61.891
                },
                {
                    "x": 14.279,
                    "y": 63.997
                },
                {
                    "x": 18.686,
                    "y": 63.953
                },
                {
                    "x": 18.662,
                    "y": 65.663
                },
                {
                    "x": 13.81,
                    "y": 65.663
                }
            ]
        }
    },
    {
        "id": "bee4d5a5-384d-4544-8f39-76b29b9730c9",
        "name": "Новая зона 3",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 19.811,
                    "y": 65.707
                },
                {
                    "x": 19.787,
                    "y": 63.865
                },
                {
                    "x": 24.288,
                    "y": 63.821
                },
                {
                    "x": 24.288,
                    "y": 64.962
                },
                {
                    "x": 22.178,
                    "y": 65.093
                },
                {
                    "x": 22.085,
                    "y": 65.927
                }
            ]
        }
    },
    {
        "id": "68149406-a5b1-4bcb-bb16-cb2ac95ce31e",
        "name": "3",
        "status": "occupied",
        "description": "раздевалка + тренерская",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 19.858,
                    "y": 67.549
                },
                {
                    "x": 25.108,
                    "y": 67.593
                },
                {
                    "x": 25.19,
                    "y": 76.967
                },
                {
                    "x": 25.049,
                    "y": 78.941
                },
                {
                    "x": 24.557,
                    "y": 79.248
                },
                {
                    "x": 24.627,
                    "y": 80.169
                },
                {
                    "x": 22.517,
                    "y": 80.213
                },
                {
                    "x": 22.588,
                    "y": 79.248
                },
                {
                    "x": 23.338,
                    "y": 78.722
                },
                {
                    "x": 23.291,
                    "y": 76.178
                },
                {
                    "x": 21.415,
                    "y": 76.353
                },
                {
                    "x": 19.845,
                    "y": 76.178
                }
            ]
        },
        "tenant": "ТОО ПРОФ ХК АЛМАТЫ",
        "area": 164.9
    },
    {
        "id": "49e9efa6-d0e6-411e-8e35-97376ca13bbf",
        "name": "Новая зона 5",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 25.929,
                    "y": 65.093
                },
                {
                    "x": 25.929,
                    "y": 63.865
                },
                {
                    "x": 30.453,
                    "y": 63.821
                },
                {
                    "x": 30.453,
                    "y": 65.751
                },
                {
                    "x": 28.062,
                    "y": 65.795
                },
                {
                    "x": 28.062,
                    "y": 65.049
                }
            ]
        }
    },
    {
        "id": "d5ebf7fa-752b-461f-b682-cb5104a212b1",
        "name": "133",
        "status": "occupied",
        "description": "склад",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 31.555,
                    "y": 65.707
                },
                {
                    "x": 31.531,
                    "y": 63.909
                },
                {
                    "x": 35.938,
                    "y": 63.777
                },
                {
                    "x": 36.923,
                    "y": 61.848
                },
                {
                    "x": 37.603,
                    "y": 63.163
                },
                {
                    "x": 36.36,
                    "y": 65.839
                }
            ]
        },
        "tenant": "XK TENGRI",
        "area": 22
    },
    {
        "id": "f0d0c0a3-d97c-403b-9852-a3eee1ba1ac8",
        "name": "134",
        "status": "occupied",
        "description": "склад",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 37.04,
                    "y": 61.848
                },
                {
                    "x": 37.649,
                    "y": 63.032
                },
                {
                    "x": 39.431,
                    "y": 59.655
                },
                {
                    "x": 39.337,
                    "y": 58.295
                },
                {
                    "x": 38.423,
                    "y": 58.12
                },
                {
                    "x": 38.423,
                    "y": 59.172
                }
            ]
        },
        "tenant": "XK TENGI",
        "area": 22
    },
    {
        "id": "dec69c4b-c0f4-4b2d-aed7-76f1e234b08c",
        "name": "135",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 37.907,
                    "y": 65.444
                },
                {
                    "x": 38.962,
                    "y": 63.383
                },
                {
                    "x": 42.877,
                    "y": 67.944
                },
                {
                    "x": 43.744,
                    "y": 68.207
                },
                {
                    "x": 42.549,
                    "y": 70.707
                },
                {
                    "x": 40.58,
                    "y": 73.47
                }
            ]
        },
        "tenant": "UNITED ALMATY HOCKEY CLUB",
        "area": 39.5
    },
    {
        "id": "50f77e70-9ad3-489f-817e-6bf4ff7a00b7",
        "name": "Новая зона 9",
        "status": "vacant",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 37.392,
                    "y": 74.567
                },
                {
                    "x": 36.712,
                    "y": 72.286
                },
                {
                    "x": 36.665,
                    "y": 71.409
                },
                {
                    "x": 35.118,
                    "y": 71.584
                },
                {
                    "x": 35.141,
                    "y": 70.225
                },
                {
                    "x": 34.368,
                    "y": 70.269
                },
                {
                    "x": 34.368,
                    "y": 71.453
                },
                {
                    "x": 32.024,
                    "y": 71.584
                },
                {
                    "x": 32,
                    "y": 73.427
                },
                {
                    "x": 34.18,
                    "y": 73.47
                },
                {
                    "x": 34.227,
                    "y": 75.663
                },
                {
                    "x": 33.688,
                    "y": 75.751
                },
                {
                    "x": 34.039,
                    "y": 77.286
                }
            ]
        }
    },
    {
        "id": "163ff85d-451b-4164-8437-8eca65245f76",
        "name": "Кафе",
        "status": "occupied",
        "description": "ДСС/ХА/ААА/точкапитания/2025",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 45.312,
                    "y": 41.535
                },
                {
                    "x": 45.757,
                    "y": 44.342
                },
                {
                    "x": 46.132,
                    "y": 44.43
                },
                {
                    "x": 46.179,
                    "y": 46.754
                },
                {
                    "x": 46.859,
                    "y": 46.798
                },
                {
                    "x": 46.882,
                    "y": 45.482
                },
                {
                    "x": 48.429,
                    "y": 45.351
                },
                {
                    "x": 48.523,
                    "y": 46.667
                },
                {
                    "x": 49.156,
                    "y": 46.798
                },
                {
                    "x": 49.25,
                    "y": 52.105
                },
                {
                    "x": 60.338,
                    "y": 52.325
                },
                {
                    "x": 60.291,
                    "y": 49.868
                },
                {
                    "x": 62.109,
                    "y": 49.818
                },
                {
                    "x": 62.016,
                    "y": 47.757
                },
                {
                    "x": 62.062,
                    "y": 44.511
                },
                {
                    "x": 62.109,
                    "y": 41.397
                }
            ]
        },
        "tenant": "ИП AAA",
        "area": 518.9
    },
    {
        "id": "300183a1-19f2-4915-8f2e-79d29167930b",
        "name": "Новая зона 2",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 49.414,
                    "y": 55.099
                },
                {
                    "x": 49.414,
                    "y": 61.623
                },
                {
                    "x": 51.26,
                    "y": 61.623
                },
                {
                    "x": 51.26,
                    "y": 65.022
                },
                {
                    "x": 52.608,
                    "y": 64.857
                },
                {
                    "x": 54.425,
                    "y": 64.803
                },
                {
                    "x": 54.219,
                    "y": 63.542
                },
                {
                    "x": 54.161,
                    "y": 55.154
                }
            ]
        }
    },
    {
        "id": "56f4ccff-a087-42d9-92f3-26a75aa40813",
        "name": "220/221",
        "status": "occupied",
        "description": "ДСС/ХА/QAZPRO/магазин/2025",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 56.552,
                    "y": 35.197
                },
                {
                    "x": 56.575,
                    "y": 38.925
                },
                {
                    "x": 58.849,
                    "y": 38.925
                },
                {
                    "x": 58.849,
                    "y": 36.864
                },
                {
                    "x": 58.615,
                    "y": 36.82
                },
                {
                    "x": 58.662,
                    "y": 34.715
                }
            ]
        },
        "tenant": "ИП \"QAZPRO\"",
        "area": 37.7
    },
    {
        "id": "05f6011d-4ae9-4981-965e-d316fa736c6f",
        "name": "223",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 60.091,
                    "y": 38.794
                },
                {
                    "x": 60.068,
                    "y": 37.741
                },
                {
                    "x": 60.631,
                    "y": 37.741
                },
                {
                    "x": 60.654,
                    "y": 39.145
                }
            ]
        }
    },
    {
        "id": "50effe46-3b86-4627-83dc-03c638a01da9",
        "name": "Новая зона 5",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 61.605,
                    "y": 55.146
                },
                {
                    "x": 61.629,
                    "y": 57.734
                },
                {
                    "x": 64.067,
                    "y": 57.69
                },
                {
                    "x": 63.973,
                    "y": 55.102
                }
            ]
        }
    },
    {
        "id": "f244fd92-33cc-4144-9a94-47faf204befc",
        "name": "Новая зона 6",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 69.2,
                    "y": 64.576
                },
                {
                    "x": 69.153,
                    "y": 67.119
                },
                {
                    "x": 70.935,
                    "y": 67.119
                },
                {
                    "x": 70.958,
                    "y": 64.488
                }
            ]
        }
    },
    {
        "id": "8cc8ca70-724e-4b7a-ac1a-b93c03c7439c",
        "name": "Новая зона 7",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 75.014,
                    "y": 68.128
                },
                {
                    "x": 74.99,
                    "y": 70.935
                },
                {
                    "x": 76.678,
                    "y": 70.935
                },
                {
                    "x": 76.655,
                    "y": 68.172
                }
            ]
        }
    },
    {
        "id": "57d16a9f-0d30-4251-a453-868c93d4c682",
        "name": "270",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 78.811,
                    "y": 68.216
                },
                {
                    "x": 80.475,
                    "y": 68.216
                },
                {
                    "x": 80.475,
                    "y": 70.891
                },
                {
                    "x": 78.858,
                    "y": 70.891
                }
            ]
        }
    },
    {
        "id": "addab000-f3c5-450e-804b-893010e54ac1",
        "name": "255",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 72.693,
                    "y": 72.953
                },
                {
                    "x": 73.724,
                    "y": 72.953
                },
                {
                    "x": 73.701,
                    "y": 75.014
                },
                {
                    "x": 72.716,
                    "y": 74.619
                }
            ]
        }
    },
    {
        "id": "488e0af7-b3b8-4269-8687-65bae50f88fe",
        "name": "274-276",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 77.709,
                    "y": 73.962
                },
                {
                    "x": 79.069,
                    "y": 73.874
                },
                {
                    "x": 79.069,
                    "y": 72.909
                },
                {
                    "x": 81.741,
                    "y": 72.909
                },
                {
                    "x": 81.788,
                    "y": 73.918
                },
                {
                    "x": 82.773,
                    "y": 73.83
                },
                {
                    "x": 82.796,
                    "y": 74.751
                },
                {
                    "x": 80.827,
                    "y": 75.365
                },
                {
                    "x": 78.788,
                    "y": 75.716
                },
                {
                    "x": 77.756,
                    "y": 75.76
                }
            ]
        }
    },
    {
        "id": "1af3dd00-7dc6-4546-b06a-fe663c42bb71",
        "name": "285",
        "status": "occupied",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 87.297,
                    "y": 64.532
                },
                {
                    "x": 84.484,
                    "y": 64.532
                },
                {
                    "x": 84.484,
                    "y": 67.163
                },
                {
                    "x": 86.289,
                    "y": 67.119
                },
                {
                    "x": 86.312,
                    "y": 66.155
                },
                {
                    "x": 87.297,
                    "y": 66.242
                }
            ]
        }
    },
    {
        "id": "81c1e7d8-9aed-4ad3-9e3e-f4c06211e201",
        "name": "301",
        "status": "occupied",
        "description": "ДСС/ХА/МЕДВЕДИ/тренерская/2025\nтренерская",
        "tenant": "ТОО СК Медведи",
        "area": 29.4,
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 91.585,
                    "y": 55.877
                },
                {
                    "x": 91.538,
                    "y": 54.211
                },
                {
                    "x": 93.882,
                    "y": 54.079
                },
                {
                    "x": 93.905,
                    "y": 55.921
                }
            ]
        }
    },
    {
        "id": "b32f44a4-d88e-4432-89a9-1b6cd28dc605",
        "name": "304,305",
        "status": "occupied",
        "description": "ДСС/ХА/МЕДВЕДИ/тренерская/2025",
        "tenant": "ТОО СК МЕДВЕДИ",
        "area": 27.4,
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 95.312,
                    "y": 57.675
                },
                {
                    "x": 95.405,
                    "y": 51.38
                },
                {
                    "x": 96.905,
                    "y": 51.423
                },
                {
                    "x": 96.835,
                    "y": 54.055
                },
                {
                    "x": 96.249,
                    "y": 57.739
                }
            ]
        }
    },
    {
        "id": "e17f205c-0fef-4edd-bb74-76fd08b94255",
        "name": "Раздевалки",
        "status": "changingRoom",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 86.738,
                    "y": 72.459
                },
                {
                    "x": 86.293,
                    "y": 70.617
                },
                {
                    "x": 86.34,
                    "y": 66.319
                },
                {
                    "x": 87.301,
                    "y": 66.275
                },
                {
                    "x": 87.348,
                    "y": 64.564
                },
                {
                    "x": 87.981,
                    "y": 64.564
                },
                {
                    "x": 89.129,
                    "y": 63.424
                },
                {
                    "x": 90.348,
                    "y": 61.582
                },
                {
                    "x": 91.145,
                    "y": 59.126
                },
                {
                    "x": 91.567,
                    "y": 56.055
                },
                {
                    "x": 93.911,
                    "y": 56.012
                },
                {
                    "x": 93.911,
                    "y": 54.213
                },
                {
                    "x": 95.247,
                    "y": 54.082
                },
                {
                    "x": 95.224,
                    "y": 57.678
                },
                {
                    "x": 95.951,
                    "y": 57.854
                },
                {
                    "x": 95.294,
                    "y": 60.266
                },
                {
                    "x": 94.614,
                    "y": 62.371
                },
                {
                    "x": 93.466,
                    "y": 64.783
                },
                {
                    "x": 92.317,
                    "y": 66.669
                },
                {
                    "x": 90.254,
                    "y": 69.213
                },
                {
                    "x": 88.613,
                    "y": 71.012
                }
            ]
        }
    },
    {
        "id": "16f24487-7779-449f-a110-e4b653575e2f",
        "name": "Раздевалка",
        "status": "changingRoom",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 78.809,
                    "y": 68.046
                },
                {
                    "x": 78.832,
                    "y": 64.45
                },
                {
                    "x": 84.411,
                    "y": 64.581
                },
                {
                    "x": 84.411,
                    "y": 67.169
                },
                {
                    "x": 86.24,
                    "y": 67.169
                },
                {
                    "x": 86.24,
                    "y": 69.713
                },
                {
                    "x": 82.817,
                    "y": 69.844
                },
                {
                    "x": 82.841,
                    "y": 70.853
                },
                {
                    "x": 80.45,
                    "y": 70.765
                },
                {
                    "x": 80.473,
                    "y": 68.265
                }
            ]
        }
    },
    {
        "id": "a3edafae-7b27-4230-b2ea-259240352a68",
        "name": "306,307",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 91.537,
                    "y": 51.116
                },
                {
                    "x": 91.561,
                    "y": 53.967
                },
                {
                    "x": 95.265,
                    "y": 54.011
                },
                {
                    "x": 95.218,
                    "y": 51.336
                }
            ]
        }
    },
    {
        "id": "7d2d1b98-88f2-4cb0-853c-7b3529469c45",
        "name": "314-317",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 91.481,
                    "y": 44.511
                },
                {
                    "x": 94.364,
                    "y": 44.467
                },
                {
                    "x": 94.388,
                    "y": 49.73
                },
                {
                    "x": 91.551,
                    "y": 49.73
                }
            ]
        }
    },
    {
        "id": "9fd29718-63d9-4c54-82be-a525b04c01a5",
        "name": "327",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 95.794,
                    "y": 41.002
                },
                {
                    "x": 95.372,
                    "y": 41.309
                },
                {
                    "x": 91.411,
                    "y": 41.222
                },
                {
                    "x": 91.411,
                    "y": 32.669
                },
                {
                    "x": 89.7,
                    "y": 32.625
                },
                {
                    "x": 89.653,
                    "y": 31.529
                },
                {
                    "x": 89.067,
                    "y": 31.573
                },
                {
                    "x": 89.043,
                    "y": 30.476
                },
                {
                    "x": 87.637,
                    "y": 30.476
                },
                {
                    "x": 87.566,
                    "y": 27.932
                },
                {
                    "x": 89.442,
                    "y": 29.599
                },
                {
                    "x": 90.965,
                    "y": 31.134
                },
                {
                    "x": 92.489,
                    "y": 32.888
                },
                {
                    "x": 93.872,
                    "y": 35.257
                },
                {
                    "x": 94.997,
                    "y": 37.713
                }
            ]
        }
    },
    {
        "id": "e1727ad9-58fd-4aa0-85c0-4ffee9e81fd7",
        "name": "325",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 87.52,
                    "y": 31.66
                },
                {
                    "x": 84.871,
                    "y": 31.704
                },
                {
                    "x": 84.871,
                    "y": 32.45
                },
                {
                    "x": 85.621,
                    "y": 34.029
                },
                {
                    "x": 87.52,
                    "y": 34.073
                }
            ]
        }
    },
    {
        "id": "1005b9a5-0f74-4a2d-9d0e-68626ed6cce7",
        "name": "с/у",
        "status": "sanitaryFacilities",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 85.668,
                    "y": 34.029
                },
                {
                    "x": 87.543,
                    "y": 34.073
                },
                {
                    "x": 87.613,
                    "y": 30.52
                },
                {
                    "x": 88.973,
                    "y": 30.52
                },
                {
                    "x": 88.973,
                    "y": 31.573
                },
                {
                    "x": 89.606,
                    "y": 31.573
                },
                {
                    "x": 89.629,
                    "y": 32.625
                },
                {
                    "x": 91.34,
                    "y": 32.801
                },
                {
                    "x": 91.364,
                    "y": 38.108
                },
                {
                    "x": 90.051,
                    "y": 38.239
                },
                {
                    "x": 89.278,
                    "y": 37.011
                },
                {
                    "x": 88.082,
                    "y": 35.608
                },
                {
                    "x": 86.863,
                    "y": 35.081
                },
                {
                    "x": 86.183,
                    "y": 35.081
                }
            ]
        }
    },
    {
        "id": "e6141705-6146-4446-932b-c4787d90eea0",
        "name": "334,335",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 77.698,
                    "y": 35.915
                },
                {
                    "x": 77.604,
                    "y": 33.853
                },
                {
                    "x": 82.878,
                    "y": 33.722
                },
                {
                    "x": 84.003,
                    "y": 35.958
                }
            ]
        }
    },
    {
        "id": "6430a8a1-e529-471f-b4c9-6b3ce0dbe987",
        "name": "340",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 76.596,
                    "y": 35.915
                },
                {
                    "x": 71.181,
                    "y": 36.002
                },
                {
                    "x": 72.377,
                    "y": 33.765
                },
                {
                    "x": 76.596,
                    "y": 33.722
                }
            ]
        }
    },
    {
        "id": "a5347846-d40d-4871-9c75-0ced636c58b8",
        "name": "333",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 80.815,
                    "y": 28.064
                },
                {
                    "x": 82.808,
                    "y": 28.064
                },
                {
                    "x": 82.808,
                    "y": 29.994
                },
                {
                    "x": 80.815,
                    "y": 30.037
                }
            ]
        }
    },
    {
        "id": "c20e5422-03df-4457-a5b1-f8cf611f7bd4",
        "name": "АУП",
        "status": "reserved",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 80.745,
                    "y": 24.467
                },
                {
                    "x": 80.769,
                    "y": 27.932
                },
                {
                    "x": 82.738,
                    "y": 27.976
                },
                {
                    "x": 82.761,
                    "y": 25.213
                }
            ]
        }
    },
    {
        "id": "5fdbd26f-7e88-40c2-927b-1ee4d0458fbb",
        "name": "АУП",
        "status": "reserved",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 63.516,
                    "y": 32.45
                },
                {
                    "x": 63.445,
                    "y": 33.415
                },
                {
                    "x": 65.204,
                    "y": 33.371
                },
                {
                    "x": 65.133,
                    "y": 38.59
                },
                {
                    "x": 65.836,
                    "y": 37.318
                },
                {
                    "x": 66.516,
                    "y": 36.397
                },
                {
                    "x": 67.243,
                    "y": 35.783
                },
                {
                    "x": 68.11,
                    "y": 35.257
                },
                {
                    "x": 69.306,
                    "y": 35.081
                },
                {
                    "x": 70.642,
                    "y": 32.625
                },
                {
                    "x": 70.665,
                    "y": 29.95
                },
                {
                    "x": 70.032,
                    "y": 29.906
                },
                {
                    "x": 70.079,
                    "y": 28.151
                },
                {
                    "x": 69.704,
                    "y": 26.66
                },
                {
                    "x": 67.571,
                    "y": 28.108
                },
                {
                    "x": 65.321,
                    "y": 30.169
                }
            ]
        }
    },
    {
        "id": "ce3f9e90-3097-41f4-93a5-37299bdcb7b7",
        "name": "с/у",
        "status": "sanitaryFacilities",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 61.406,
                    "y": 33.853
                },
                {
                    "x": 63.07,
                    "y": 33.195
                },
                {
                    "x": 63.469,
                    "y": 32.494
                },
                {
                    "x": 63.469,
                    "y": 33.371
                },
                {
                    "x": 65.11,
                    "y": 33.502
                },
                {
                    "x": 65.063,
                    "y": 38.809
                },
                {
                    "x": 61.945,
                    "y": 38.853
                },
                {
                    "x": 61.875,
                    "y": 35.52
                },
                {
                    "x": 61.383,
                    "y": 35.476
                }
            ]
        }
    },
    {
        "id": "53e93719-7af5-4e83-975c-03a69005105d",
        "name": "1",
        "status": "occupied",
        "description": "ДСС/ХА/UNIQUE-KZ/зал/2024\nзаточка",
        "tenant": "ТОО UNIQUE-KZ",
        "area": 3.9,
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 62.18,
                    "y": 42.888
                },
                {
                    "x": 62.18,
                    "y": 41.485
                },
                {
                    "x": 62.953,
                    "y": 41.265
                },
                {
                    "x": 63.024,
                    "y": 42.932
                }
            ]
        }
    },
    {
        "id": "6ef586c7-e75c-4931-b564-459ecb486772",
        "name": "169",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 41.856,
                    "y": 29.687
                },
                {
                    "x": 41.856,
                    "y": 38.809
                },
                {
                    "x": 44.622,
                    "y": 38.809
                },
                {
                    "x": 44.716,
                    "y": 37.055
                },
                {
                    "x": 46.451,
                    "y": 37.011
                },
                {
                    "x": 46.498,
                    "y": 35.651
                },
                {
                    "x": 48.138,
                    "y": 36.353
                },
                {
                    "x": 48.115,
                    "y": 38.765
                },
                {
                    "x": 51.139,
                    "y": 38.853
                },
                {
                    "x": 51.139,
                    "y": 34.73
                },
                {
                    "x": 49.381,
                    "y": 34.204
                },
                {
                    "x": 47.084,
                    "y": 33.458
                },
                {
                    "x": 44.927,
                    "y": 32.099
                }
            ]
        }
    },
    {
        "id": "f0539c43-3a60-4bf9-85b6-faeddd5ed054",
        "name": "180",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 42.747,
                    "y": 44.511
                },
                {
                    "x": 42.747,
                    "y": 43.59
                },
                {
                    "x": 41.903,
                    "y": 43.502
                },
                {
                    "x": 41.833,
                    "y": 45.871
                },
                {
                    "x": 42.723,
                    "y": 45.915
                },
                {
                    "x": 42.794,
                    "y": 52.23
                },
                {
                    "x": 49.123,
                    "y": 52.274
                },
                {
                    "x": 49.146,
                    "y": 46.88
                },
                {
                    "x": 48.537,
                    "y": 46.88
                },
                {
                    "x": 48.42,
                    "y": 45.52
                },
                {
                    "x": 47.013,
                    "y": 45.432
                },
                {
                    "x": 47.013,
                    "y": 46.88
                },
                {
                    "x": 46.099,
                    "y": 46.923
                },
                {
                    "x": 45.911,
                    "y": 44.38
                }
            ]
        }
    },
    {
        "id": "b3b7887d-5707-46af-a3a5-a54924502f40",
        "name": "144",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 38.457,
                    "y": 58.02
                },
                {
                    "x": 39.301,
                    "y": 58.108
                },
                {
                    "x": 39.325,
                    "y": 53.853
                },
                {
                    "x": 38.41,
                    "y": 53.853
                }
            ]
        }
    },
    {
        "id": "22495860-3c67-486d-bd33-893b735803b9",
        "name": "150",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 38.387,
                    "y": 47.537
                },
                {
                    "x": 39.371,
                    "y": 47.45
                },
                {
                    "x": 39.371,
                    "y": 51.792
                },
                {
                    "x": 38.34,
                    "y": 51.88
                }
            ]
        }
    },
    {
        "id": "ba589093-4781-4a2c-9f38-a87dd91c01a8",
        "name": "148",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 38.34,
                    "y": 44.687
                },
                {
                    "x": 38.387,
                    "y": 40.958
                },
                {
                    "x": 38.035,
                    "y": 40.301
                },
                {
                    "x": 38.738,
                    "y": 38.985
                },
                {
                    "x": 39.348,
                    "y": 40.081
                },
                {
                    "x": 39.371,
                    "y": 44.818
                }
            ]
        }
    },
    {
        "id": "afc31ab3-33ff-4488-a992-ec129c97e606",
        "name": "c/y",
        "status": "sanitaryFacilities",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 35.152,
                    "y": 31.616
                },
                {
                    "x": 37.332,
                    "y": 31.573
                },
                {
                    "x": 39.395,
                    "y": 35.301
                },
                {
                    "x": 37.895,
                    "y": 38.02
                },
                {
                    "x": 36.16,
                    "y": 34.818
                },
                {
                    "x": 35.152,
                    "y": 34.774
                }
            ]
        }
    },
    {
        "id": "37ec1849-b77e-46ee-abba-59cc4bc34418",
        "name": "12",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 31.519,
                    "y": 31.485
                },
                {
                    "x": 35.082,
                    "y": 31.485
                },
                {
                    "x": 35.199,
                    "y": 34.774
                },
                {
                    "x": 36.183,
                    "y": 34.862
                },
                {
                    "x": 37.824,
                    "y": 37.932
                },
                {
                    "x": 37.355,
                    "y": 38.897
                },
                {
                    "x": 35.926,
                    "y": 35.871
                },
                {
                    "x": 31.566,
                    "y": 36.002
                }
            ]
        }
    },
    {
        "id": "e27ccd7b-4eb2-4eaf-b502-d942d26156a9",
        "name": "15",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 19.845,
                    "y": 35.915
                },
                {
                    "x": 19.845,
                    "y": 34.16
                },
                {
                    "x": 30.347,
                    "y": 33.941
                },
                {
                    "x": 30.393,
                    "y": 35.958
                }
            ]
        }
    },
    {
        "id": "8708fe9c-62a7-4e67-9af2-ee636b5cbac6",
        "name": "c/y",
        "status": "sanitaryFacilities",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 10.82,
                    "y": 35.432
                },
                {
                    "x": 12.906,
                    "y": 31.529
                },
                {
                    "x": 15.063,
                    "y": 31.573
                },
                {
                    "x": 15.039,
                    "y": 34.599
                },
                {
                    "x": 14.078,
                    "y": 34.687
                },
                {
                    "x": 12.32,
                    "y": 38.064
                }
            ]
        }
    },
    {
        "id": "90a511ce-9aa0-4382-895d-f74ca5a382eb",
        "name": "20",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 12.414,
                    "y": 38.195
                },
                {
                    "x": 14.055,
                    "y": 34.862
                },
                {
                    "x": 15.086,
                    "y": 34.774
                },
                {
                    "x": 15.157,
                    "y": 31.66
                },
                {
                    "x": 18.603,
                    "y": 31.704
                },
                {
                    "x": 18.673,
                    "y": 35.915
                },
                {
                    "x": 14.289,
                    "y": 36.002
                },
                {
                    "x": 12.766,
                    "y": 38.985
                }
            ]
        }
    },
    {
        "id": "290f4f30-ebd1-4681-b080-8f3b0bac9703",
        "name": "c/y",
        "status": "sanitaryFacilities",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 7.491,
                    "y": 44.906
                },
                {
                    "x": 8.476,
                    "y": 44.906
                },
                {
                    "x": 8.523,
                    "y": 46.923
                },
                {
                    "x": 7.445,
                    "y": 46.836
                }
            ]
        }
    },
    {
        "id": "559cb660-68db-47fd-8b71-9d0736f1e1af",
        "name": "30",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 11.383,
                    "y": 38.722
                },
                {
                    "x": 12.133,
                    "y": 40.081
                },
                {
                    "x": 11.851,
                    "y": 40.783
                },
                {
                    "x": 11.875,
                    "y": 44.643
                },
                {
                    "x": 10.703,
                    "y": 44.687
                },
                {
                    "x": 10.773,
                    "y": 40.037
                }
            ]
        }
    },
    {
        "id": "bd9c726f-00ec-40fc-8a2c-6278789c5164",
        "name": "51",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 10.773,
                    "y": 46.09
                },
                {
                    "x": 11.851,
                    "y": 46.002
                },
                {
                    "x": 11.758,
                    "y": 55.476
                },
                {
                    "x": 10.797,
                    "y": 55.476
                }
            ]
        }
    },
    {
        "id": "da9acb19-7485-4266-a2fa-22110bc40a1a",
        "name": "Конференц-зал",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 8.593,
                    "y": 46.748
                },
                {
                    "x": 9.46,
                    "y": 46.792
                },
                {
                    "x": 9.46,
                    "y": 55.213
                },
                {
                    "x": 3.858,
                    "y": 55.388
                },
                {
                    "x": 3.506,
                    "y": 53.327
                },
                {
                    "x": 3.319,
                    "y": 50.081
                },
                {
                    "x": 5.218,
                    "y": 50.213
                },
                {
                    "x": 5.241,
                    "y": 48.458
                },
                {
                    "x": 8.617,
                    "y": 48.634
                }
            ]
        }
    },
    {
        "id": "669f2dac-b596-4e4d-8826-35f2e88464ea",
        "name": "54",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 4.303,
                    "y": 58.897
                },
                {
                    "x": 4.866,
                    "y": 58.327
                },
                {
                    "x": 6.859,
                    "y": 58.195
                },
                {
                    "x": 6.929,
                    "y": 59.687
                },
                {
                    "x": 4.913,
                    "y": 61.309
                }
            ]
        }
    },
    {
        "id": "bf227065-c31b-431d-8c8d-a4779ed3eaae",
        "name": "51",
        "status": "auxiliaryRooms",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 9.46,
                    "y": 59.906
                },
                {
                    "x": 9.507,
                    "y": 62.362
                },
                {
                    "x": 10.468,
                    "y": 61.704
                }
            ]
        }
    },
    {
        "id": "8afbb12e-f032-4dd8-8e2a-a065e8380d91",
        "name": "Раздевалки",
        "status": "changingRoom",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 6.952,
                    "y": 58.239
                },
                {
                    "x": 9.484,
                    "y": 58.195
                },
                {
                    "x": 9.507,
                    "y": 62.494
                },
                {
                    "x": 10.515,
                    "y": 61.748
                },
                {
                    "x": 13.422,
                    "y": 67.713
                },
                {
                    "x": 18.134,
                    "y": 67.494
                },
                {
                    "x": 18.438,
                    "y": 73.502
                },
                {
                    "x": 15.977,
                    "y": 73.678
                },
                {
                    "x": 16.001,
                    "y": 75.651
                },
                {
                    "x": 17.29,
                    "y": 75.695
                },
                {
                    "x": 17.313,
                    "y": 76.88
                },
                {
                    "x": 16.821,
                    "y": 76.88
                },
                {
                    "x": 16.821,
                    "y": 77.801
                },
                {
                    "x": 14.875,
                    "y": 76.441
                },
                {
                    "x": 13.188,
                    "y": 75.257
                },
                {
                    "x": 11.336,
                    "y": 73.239
                },
                {
                    "x": 9.882,
                    "y": 71.748
                },
                {
                    "x": 8.687,
                    "y": 70.125
                },
                {
                    "x": 7.351,
                    "y": 67.844
                },
                {
                    "x": 6.038,
                    "y": 65.257
                },
                {
                    "x": 7.796,
                    "y": 63.765
                },
                {
                    "x": 6.929,
                    "y": 59.774
                }
            ]
        }
    },
    {
        "id": "f78a264a-1954-4d87-9a04-74f6ef875894",
        "name": "Раздевалка",
        "status": "changingRoom",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 24.721,
                    "y": 80.169
                },
                {
                    "x": 24.674,
                    "y": 79.204
                },
                {
                    "x": 25.236,
                    "y": 79.029
                },
                {
                    "x": 25.143,
                    "y": 67.625
                },
                {
                    "x": 30.323,
                    "y": 67.581
                },
                {
                    "x": 30.323,
                    "y": 77.801
                },
                {
                    "x": 29.503,
                    "y": 78.108
                },
                {
                    "x": 29.479,
                    "y": 76.309
                },
                {
                    "x": 26.948,
                    "y": 76.441
                },
                {
                    "x": 26.971,
                    "y": 78.722
                },
                {
                    "x": 26.573,
                    "y": 78.765
                },
                {
                    "x": 26.643,
                    "y": 80.037
                },
                {
                    "x": 25.799,
                    "y": 80.213
                }
            ]
        }
    },
    {
        "id": "11da3efa-ca26-4afd-843d-8bbd4cbc0d56",
        "name": "99",
        "status": "occupied",
        "description": "",
        "tenant": "КДЮСШ #3",
        "area": 39.7,
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 19.902,
                    "y": 76.535
                },
                {
                    "x": 23.207,
                    "y": 76.404
                },
                {
                    "x": 23.277,
                    "y": 79.079
                },
                {
                    "x": 22.574,
                    "y": 79.079
                },
                {
                    "x": 22.574,
                    "y": 79.956
                },
                {
                    "x": 21.191,
                    "y": 79.737
                },
                {
                    "x": 19.714,
                    "y": 79.211
                }
            ]
        }
    },
    {
        "id": "980f449a-3d70-4c9c-9db2-cb7a357a7ae7",
        "name": "121",
        "status": "changingRoom",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 32.035,
                    "y": 67.601
                },
                {
                    "x": 34.308,
                    "y": 67.557
                },
                {
                    "x": 34.308,
                    "y": 71.285
                },
                {
                    "x": 32.035,
                    "y": 71.461
                }
            ]
        }
    },
    {
        "id": "37f9f30d-ed9e-4c0e-8915-3ae7fef10ebd",
        "name": "5",
        "status": "changingRoom",
        "description": "",
        "shape": {
            "type": "polygon",
            "points": [
                {
                    "x": 34.332,
                    "y": 67.469
                },
                {
                    "x": 36.817,
                    "y": 67.689
                },
                {
                    "x": 37.825,
                    "y": 65.627
                },
                {
                    "x": 40.005,
                    "y": 72.162
                },
                {
                    "x": 37.543,
                    "y": 74.487
                },
                {
                    "x": 36.91,
                    "y": 72.469
                },
                {
                    "x": 36.817,
                    "y": 71.285
                },
                {
                    "x": 35.129,
                    "y": 71.329
                },
                {
                    "x": 35.129,
                    "y": 70.276
                },
                {
                    "x": 34.285,
                    "y": 70.189
                }
            ]
        }
    }
],
    },
    {
      id: 'floor-2',
      name: '2 этаж',
      level: 2,
      note: 'Офисные помещения и коворкинг с переговорными комнатами.',
      image: floorImage2,
      rooms: [
        {
          id: 'f2-room-1',
          name: 'Офис A1',
          tenant: 'TechOrigin',
          status: 'occupied',
          description: 'Офис IT-компании с собственным серверным помещением.',
          area: 180,
          shape: {
            type: 'polygon',
            points: [
              { x: 6, y: 12 },
              { x: 46, y: 12 },
              { x: 46, y: 42 },
              { x: 24, y: 46 },
              { x: 6, y: 40 },
            ],
          },
        },
        {
          id: 'f2-room-2',
          name: 'Переговорная',
          tenant: 'Общее пользование',
          status: 'reserved',
          description: 'Комната на 12 человек, бронируется через ресепшен.',
          area: 40,
          shape: {
            type: 'polygon',
            points: [
              { x: 8, y: 48 },
              { x: 28, y: 52 },
              { x: 28, y: 64 },
              { x: 8, y: 60 },
            ],
          },
        },
        {
          id: 'f2-room-3',
          name: 'Коворкинг',
          tenant: 'Общее пользование',
          status: 'vacant',
          description: 'Отрытая площадка на 36 резидентов, готова к сдаче.',
          area: 200,
          shape: {
            type: 'polygon',
            points: [
              { x: 50, y: 16 },
              { x: 92, y: 20 },
              { x: 92, y: 62 },
              { x: 74, y: 66 },
              { x: 48, y: 56 },
            ],
          },
        },
      ],
    },
  ],
};

const fallbackFloorImages = [floorImage1, floorImage2, floorImage3];

const extractShapePoints = (shape: BuildingRoomShape): PolygonPoint[] => {
  if (shape.type === 'polygon') {
    return shape.points ?? [];
  }

  return [
    { x: shape.x, y: shape.y },
    { x: shape.x + shape.width, y: shape.y },
    { x: shape.x + shape.width, y: shape.y + shape.height },
    { x: shape.x, y: shape.y + shape.height },
  ];
};

const computeBoundingBox = (points: PolygonPoint[]) => {
  if (!points.length) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }

  return points.reduce(
    (acc, point) => ({
      minX: Math.min(acc.minX, point.x),
      minY: Math.min(acc.minY, point.y),
      maxX: Math.max(acc.maxX, point.x),
      maxY: Math.max(acc.maxY, point.y),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    },
  );
};

const computeCentroid = (points: PolygonPoint[]): PolygonPoint => {
  if (!points.length) {
    return { x: 0, y: 0 };
  }

  if (points.length === 1) {
    return points[0];
  }

  let area = 0;
  let cx = 0;
  let cy = 0;

  for (let i = 0; i < points.length; i += 1) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    const cross = current.x * next.y - next.x * current.y;
    area += cross;
    cx += (current.x + next.x) * cross;
    cy += (current.y + next.y) * cross;
  }

  area *= 0.5;
  if (Math.abs(area) < 1e-6) {
    const { minX, minY, maxX, maxY } = computeBoundingBox(points);
    return {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
    };
  }

  cx /= 6 * area;
  cy /= 6 * area;

  return { x: cx, y: cy };
};

const applyPointsToRoom = (room: BuildingRoom, points: PolygonPoint[]) => {
  if (room.shape.type === 'polygon') {
    room.shape.points = points;
    return;
  }

  const { minX, minY, maxX, maxY } = computeBoundingBox(points);
  room.shape = {
    type: 'rect',
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

const clonePlan = (plan: BuildingPlan): BuildingPlan =>
  JSON.parse(JSON.stringify(plan ?? { floors: [] }));

const clampValue = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const generateId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2, 11);
};

const modal = useModalStore();

const modalProps = computed<BuildingModalProps>(() => (modal.modalProps || {}) as BuildingModalProps);

const buildingPlan = computed<BuildingPlan>(() => {
  const providedPlan =
    modalProps.value?.buildingPlan ??
    (modalProps.value?.polygon?.meta?.buildingPlan as BuildingPlan | undefined);

  if (providedPlan?.floors?.length) {
    return providedPlan;
  }

  return fallbackBuildingPlan;
});

const buildingTitle = computed(
  () => modalProps.value?.polygon?.meta?.title ?? buildingPlan.value.name ?? 'Здание',
);

const editablePlan = ref<BuildingPlan>(clonePlan(buildingPlan.value));

watch(
  buildingPlan,
  (next) => {
    editablePlan.value = clonePlan(next);
    console.log('editablePlan.value', editablePlan.value);
  },
  { deep: true },
);

const floors = computed<BuildingFloor[]>(() => editablePlan.value.floors ?? []);
const selectedFloorId = ref<string | null>(null);
const activeFloorIndex = computed(() => {
  if (!selectedFloorId.value) {
    return -1;
  }
  return floors.value.findIndex((floor) => floor.id === selectedFloorId.value);
});

watch(
  floors,
  (nextFloors) => {
    if (!nextFloors.length) {
      selectedFloorId.value = null;
      return;
    }

    if (!selectedFloorId.value || !nextFloors.some((floor) => floor.id === selectedFloorId.value)) {
      selectedFloorId.value = nextFloors[0].id;
    }
  },
  { immediate: true },
);

const activeFloor = computed<BuildingFloor | null>(() => {
  if (!selectedFloorId.value) {
    return null;
  }
  return floors.value.find((floor) => floor.id === selectedFloorId.value) ?? null;
});

const floorRooms = computed<BuildingRoom[]>(() => activeFloor.value?.rooms ?? []);

const totalFloorArea = computed(() =>
  floorRooms.value.reduce((acc, room) => acc + (room.area ?? 0), 0),
);

const floorImageSource = computed(() => {
  if (activeFloor.value?.image) {
    return activeFloor.value.image;
  }
  const index = activeFloorIndex.value;
  if (index >= 0 && index < fallbackFloorImages.length) {
    return fallbackFloorImages[index];
  }
  return '';
});

const [floorImageElement] = useImage(() => floorImageSource.value);

const backgroundImageConfig = computed(() => {
  const image = floorImageElement.value;
  if (!image) {
    return {
      visible: false,
      x: 0,
      y: 0,
      width: stageSize.width,
      height: stageSize.height,
    };
  }

  const imageRatio = image.width / image.height;
  const stageRatio = stageSize.width / stageSize.height;

  let width = stageSize.width;
  let height = stageSize.height;
  let x = 0;
  let y = 0;

  if (imageRatio > stageRatio) {
    width = stageSize.width;
    height = stageSize.width / imageRatio;
    y = (stageSize.height - height) / 2;
  } else {
    height = stageSize.height;
    width = stageSize.height * imageRatio;
    x = (stageSize.width - width) / 2;
  }

  return {
    visible: true,
    x,
    y,
    width,
    height,
  };
});

const planContainer = ref<HTMLElement | null>(null);
type StageComponent = {
  getStage: () => StageType;
};

const stageRef = ref<StageComponent | null>(null);
const stageSize = reactive({ width: 920, height: 690 });
const aspectRatio = 4 / 3;
const stageScale = ref(1);
const stagePosition = reactive({ x: 0, y: 0 });
const MIN_SCALE = 0.6;
const MAX_SCALE = 3;
const SCALE_STEP = 1.25;

useResizeObserver(planContainer, (entries) => {
  const entry = entries.at(0);
  if (!entry) {
    return;
  }

  const width = entry.contentRect.width;
  stageSize.width = width;
  stageSize.height = clampValue(width / aspectRatio, 360, 760);
});

const toStagePoint = (point: PolygonPoint) => ({
  x: (point.x / 100) * stageSize.width,
  y: (point.y / 100) * stageSize.height,
});

const toNormalizedPoint = (point: { x: number; y: number }): PolygonPoint => ({
  x: Number(clampValue((point.x / stageSize.width) * 100, 0, 100).toFixed(3)),
  y: Number(clampValue((point.y / stageSize.height) * 100, 0, 100).toFixed(3)),
});

const clampScale = (value: number) => clampValue(value, MIN_SCALE, MAX_SCALE);

// Keep room labels readable across zoom levels
const labelFontSize = computed(() => {
  const base = 10; // base font at scale = 1
  // Make text shrink as you zoom in, but not below 10px and not above 24px
  const dynamic = base / Math.pow(stageScale.value || 1, 0.9);
  return clampValue(Math.round(dynamic), 10, 24);
});

const resetView = () => {
  stageScale.value = 1;
  stagePosition.x = 0;
  stagePosition.y = 0;
  hideTooltip();
  closePopover();
};

const applyZoom = (anchor: { x: number; y: number }, factor: number) => {
  const stage = stageRef.value?.getStage();
  if (!stage) {
    return;
  }

  const oldScale = stageScale.value;
  const newScale = clampScale(oldScale * factor);
  if (newScale === oldScale) {
    return;
  }

  const stageAnchorX = (anchor.x - stagePosition.x) / oldScale;
  const stageAnchorY = (anchor.y - stagePosition.y) / oldScale;

  stageScale.value = newScale;
  stagePosition.x = anchor.x - stageAnchorX * newScale;
  stagePosition.y = anchor.y - stageAnchorY * newScale;
  hideTooltip();
  closePopover();
};

const zoomIn = () => {
  applyZoom({ x: stageSize.width / 2, y: stageSize.height / 2 }, SCALE_STEP);
};

const zoomOut = () => {
  applyZoom({ x: stageSize.width / 2, y: stageSize.height / 2 }, 1 / SCALE_STEP);
};

const getPointerData = (stage: StageType | undefined | null) => {
  if (!stage) {
    return null;
  }
  const pointer = stage.getPointerPosition();
  if (!pointer) {
    return null;
  }

  const scale = stageScale.value;
  return {
    container: pointer,
    stage: {
      x: (pointer.x - stagePosition.x) / scale,
      y: (pointer.y - stagePosition.y) / scale,
    },
  };
};

const getRoomPalette = (room: BuildingRoom) =>
  ROOM_STATUS_COLORS[room.status ?? 'default'] ?? ROOM_STATUS_COLORS.default;

const gridLines = computed(() => {
  const lines: Array<{ points: number[]; dash: number[]; stroke: string; strokeWidth: number }> = [];
  const step =
    stageSize.width > 900 ? 80 : stageSize.width > 640 ? 64 : stageSize.width > 480 ? 48 : 36;

  for (let x = step; x < stageSize.width; x += step) {
    lines.push({
      points: [x, 0, x, stageSize.height],
      dash: [4, 8],
      stroke: 'rgba(148, 163, 184, 0.18)',
      strokeWidth: 1,
    });
  }

  for (let y = step; y < stageSize.height; y += step) {
    lines.push({
      points: [0, y, stageSize.width, y],
      dash: [4, 8],
      stroke: 'rgba(148, 163, 184, 0.18)',
      strokeWidth: 1,
    });
  }

  return lines;
});

const scaledRooms = computed(() =>
  floorRooms.value
    .map((room) => {
      const normalizedPoints = extractShapePoints(room.shape);
      if (!normalizedPoints.length) {
        return null;
      }

      const stagePoints = normalizedPoints.map(toStagePoint);
      const linePoints = stagePoints.flatMap((point) => [point.x, point.y]);
      const bounding = computeBoundingBox(normalizedPoints);
      const centroid = computeCentroid(normalizedPoints);

      const boundingStage = {
        minX: (bounding.minX / 100) * stageSize.width,
        minY: (bounding.minY / 100) * stageSize.height,
        maxX: (bounding.maxX / 100) * stageSize.width,
        maxY: (bounding.maxY / 100) * stageSize.height,
      };

      const palette = getRoomPalette(room);

      return {
        room,
        palette,
        linePoints,
        boundingStage,
        stagePoints,
        centroidStage: toStagePoint(centroid),
      };
    })
    .filter(Boolean) as Array<{
    room: BuildingRoom;
    palette: { bg: string; border: string; fill: string };
    linePoints: number[];
    boundingStage: { minX: number; minY: number; maxX: number; maxY: number };
    stagePoints: Array<{ x: number; y: number }>;
    centroidStage: { x: number; y: number };
  }>,
);

const selectedRoomId = ref<string | null>(null);
const selectedRoom = computed<BuildingRoom | null>(() => {
  if (!selectedRoomId.value) {
    return null;
  }
  return floorRooms.value.find((room) => room.id === selectedRoomId.value) ?? null;
});

const hoveredRoomId = ref<string | null>(null);
const isDrawingRoom = ref(false);
const isEditingRoom = ref(false);
const drawingPoints = ref<PolygonPoint[]>([]);
const drawingStagePoints = computed(() => drawingPoints.value.map(toStagePoint));
const drawingLinePoints = computed(() => drawingStagePoints.value.flatMap((p) => [p.x, p.y]));
const isDrawingValid = computed(() => drawingPoints.value.length >= 3);
const newRoomCounter = ref(1);

const tooltip = reactive<{
  visible: boolean;
  x: number;
  y: number;
  room: BuildingRoom | null;
}>({
  visible: false,
  x: 0,
  y: 0,
  room: null,
});

const popover = reactive<{
  visible: boolean;
  x: number;
  y: number;
  room: BuildingRoom | null;
}>({
  visible: false,
  x: 0,
  y: 0,
  room: null,
});

const tooltipStyle = computed(() =>
  tooltip.visible
    ? {
        left: `${tooltip.x}px`,
        top: `${tooltip.y}px`,
      }
    : {},
);

const popoverStyle = computed(() =>
  popover.visible
    ? {
        left: `${popover.x}px`,
        top: `${popover.y}px`,
      }
    : {},
);

const selectedRoomAnchors = computed(() => {
  if (!selectedRoom.value) {
    return [];
  }

  return extractShapePoints(selectedRoom.value.shape).map((point, index) => ({
    index,
    normalized: point,
    stage: toStagePoint(point),
  }));
});

const statusOptions = [
  { value: 'occupied', label: ROOM_STATUS_LABELS.occupied },
  { value: 'vacant', label: ROOM_STATUS_LABELS.vacant },
  { value: 'reserved', label: ROOM_STATUS_LABELS.reserved },
  { value: 'changingRoom', label: ROOM_STATUS_LABELS.changingRoom },
  { value: 'sanitaryFacilities', label: ROOM_STATUS_LABELS.sanitaryFacilities },
  { value: 'auxiliaryRooms', label: ROOM_STATUS_LABELS.auxiliaryRooms },
];

const legendItems = [
  { status: 'occupied', label: ROOM_STATUS_LABELS.occupied },
  { status: 'vacant', label: ROOM_STATUS_LABELS.vacant },
  { status: 'reserved', label: ROOM_STATUS_LABELS.reserved },
  { status: 'changingRoom', label: ROOM_STATUS_LABELS.changingRoom },
  { status: 'sanitaryFacilities', label: ROOM_STATUS_LABELS.sanitaryFacilities },
  { status: 'auxiliaryRooms', label: ROOM_STATUS_LABELS.auxiliaryRooms },
];

const popoverStatusModel = computed({
  get: () => popover.room?.status ?? '',
  set: (value: string) => {
    if (!popover.room) {
      return;
    }
    popover.room.status = (value || undefined) as BuildingRoom['status'] | undefined;
  },
});

const hideTooltip = () => {
  tooltip.visible = false;
  tooltip.room = null;
  hoveredRoomId.value = null;
};

const showTooltipForRoom = (room: BuildingRoom, event: KonvaEventObject<MouseEvent>) => {
  if (isDrawingRoom.value || isEditingRoom.value) {
    return;
  }

  const stage = event.target.getStage();
  const pointer = getPointerData(stage);
  if (!pointer) {
    return;
  }

  hoveredRoomId.value = room.id;
  tooltip.room = room;
  tooltip.x = clampValue(pointer.container.x + 18, 12, stageSize.width - 12);
  tooltip.y = clampValue(pointer.container.y - 12, 12, stageSize.height - 12);
  tooltip.visible = true;
};

const handleRoomPointerEnter = (room: BuildingRoom, event: KonvaEventObject<MouseEvent>) => {
  showTooltipForRoom(room, event);
};

const handleRoomPointerMove = (room: BuildingRoom, event: KonvaEventObject<MouseEvent>) => {
  showTooltipForRoom(room, event);
};

const handleRoomPointerLeave = () => {
  hideTooltip();
};

const closePopover = () => {
  popover.visible = false;
  popover.room = null;
};

const openRoomPopover = (room: BuildingRoom, event: KonvaEventObject<MouseEvent>) => {
  if (isDrawingRoom.value) {
    return;
  }

  const stage = event.target.getStage();
  const pointer = getPointerData(stage);
  if (!pointer) {
    return;
  }

  selectedRoomId.value = room.id;
  const anchorX = clampValue(pointer.container.x, 180, stageSize.width - 180);
  const anchorY = clampValue(pointer.container.y, 160, stageSize.height - 40);

  popover.x = anchorX;
  popover.y = anchorY;
  popover.room = room;
  popover.visible = true;

  hideTooltip();
};

const handleRoomClick = (room: BuildingRoom, event: KonvaEventObject<MouseEvent>) => {
  // Do not open popover while editing polygon anchors; also don't stop propagation
  // so that double-click can be detected properly on the same target
  if (isEditingRoom.value) {
    return;
  }
  event.cancelBubble = true;
  openRoomPopover(room, event);
};

const startDrawing = () => {
  isDrawingRoom.value = true;
  isEditingRoom.value = false;
  drawingPoints.value = [];
  selectedRoomId.value = null;
  hideTooltip();
  closePopover();
};

const finishDrawing = () => {
  if (!isDrawingValid.value || !selectedFloorId.value) {
    return;
  }

  const floor = editablePlan.value.floors.find((item) => item.id === selectedFloorId.value);
  if (!floor) {
    return;
  }

  const points = drawingPoints.value.map((point) => ({ ...point }));
  const newRoom: BuildingRoom = {
    id: generateId(),
    name: `Новая зона ${newRoomCounter.value}`,
    status: 'vacant',
    description: '',
    tenant: undefined,
    area: undefined,
    shape: {
      type: 'polygon',
      points,
    },
  };

  newRoomCounter.value += 1;
  floor.rooms.push(newRoom);
  selectedRoomId.value = newRoom.id;
  drawingPoints.value = [];
  isDrawingRoom.value = false;
};

const cancelDrawing = () => {
  drawingPoints.value = [];
  isDrawingRoom.value = false;
};

const undoDrawingPoint = () => {
  if (!drawingPoints.value.length) {
    return;
  }
  drawingPoints.value = drawingPoints.value.slice(0, -1);
};

const handleStageClick = (event: KonvaEventObject<MouseEvent>) => {
  if (isDrawingRoom.value) {
    const stage = event.target.getStage();
    const pointer = getPointerData(stage);
    if (!stage || !pointer) {
      return;
    }

    const normalizedPoint = toNormalizedPoint(pointer.stage);
    drawingPoints.value = [...drawingPoints.value, normalizedPoint];
    return;
  }

  if (event.target === event.target.getStage()) {
    selectedRoomId.value = null;
    hideTooltip();
    closePopover();
  }
};

const handleStageDoubleClick = () => {
  if (isDrawingRoom.value && isDrawingValid.value) {
    finishDrawing();
  }
};

const toggleEditing = () => {
  if (!selectedRoom.value) {
    return;
  }

  isEditingRoom.value = !isEditingRoom.value;
  hideTooltip();
  closePopover();

  const stage = stageRef.value?.getStage();
  if (stage) {
    stage.container().style.cursor = isEditingRoom.value ? 'pointer' : 'default';
  }
};

const deleteSelectedRoom = () => {
  if (!selectedRoomId.value || !selectedFloorId.value) {
    return;
  }

  const floor = editablePlan.value.floors.find((item) => item.id === selectedFloorId.value);
  if (!floor) {
    return;
  }

  floor.rooms = floor.rooms.filter((room) => room.id !== selectedRoomId.value);
  selectedRoomId.value = null;
  isEditingRoom.value = false;
  closePopover();
};

const boundAnchorToStage = (position: { x: number; y: number }) => ({
  x: clampValue(position.x, 0, stageSize.width),
  y: clampValue(position.y, 0, stageSize.height),
});

const handleAnchorDragMove = (index: number, event: KonvaEventObject<DragEvent>) => {
  if (!selectedRoom.value) return;

  const stage = event.target.getStage();
  const pointer = getPointerData(stage);
  if (!pointer) return;

  const normalizedPoint = toNormalizedPoint(pointer.stage);
  const nextPoints = extractShapePoints(selectedRoom.value.shape).map((point) => ({ ...point }));
  nextPoints[index] = normalizedPoint;
  applyPointsToRoom(selectedRoom.value, nextPoints);
  event.cancelBubble = true;
};

const handleAnchorDragStart = (event: KonvaEventObject<DragEvent>) => {
  const stage = event.target.getStage();
  if (stage) {
    stage.container().style.setProperty('cursor', 'grabbing');
    // Ensure the stage cannot be dragged while moving anchors
    stage.draggable(false);
  }
  event.cancelBubble = true;
};

const handleAnchorDragEnd = (event: KonvaEventObject<DragEvent>) => {
  const stage = event.target.getStage();
  if (stage) {
    stage.container().style.setProperty('cursor', 'default');
    // Restore stage draggability according to current mode
    stage.draggable(!isDrawingRoom.value && !isEditingRoom.value);
  }
  event.cancelBubble = true;
};

// --- Adding new points to polygon while editing ---
const distancePointToSegment = (p: { x: number; y: number }, a: { x: number; y: number }, b: {
  x: number;
  y: number;
}) => {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const wx = p.x - a.x;
  const wy = p.y - a.y;
  const c1 = vx * wx + vy * wy;
  if (c1 <= 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const c2 = vx * vx + vy * vy;
  if (c2 <= c1) return Math.hypot(p.x - b.x, p.y - b.y);
  const t = c1 / c2;
  const proj = { x: a.x + t * vx, y: a.y + t * vy };
  return Math.hypot(p.x - proj.x, p.y - proj.y);
};

const findNearestSegmentIndex = (stagePoints: Array<{ x: number; y: number }>, p: {
  x: number;
  y: number;
}) => {
  let minDist = Number.POSITIVE_INFINITY;
  let index = -1;
  const n = stagePoints.length;
  for (let i = 0; i < n; i += 1) {
    const a = stagePoints[i];
    const b = stagePoints[(i + 1) % n];
    const d = distancePointToSegment(p, a, b);
    if (d < minDist) {
      minDist = d;
      index = i;
    }
  }
  return index; // insert after index
};

const handlePolygonAddPoint = (room: BuildingRoom, event: KonvaEventObject<MouseEvent>) => {
  if (!isEditingRoom.value) return;
  const stage = event.target.getStage();
  const pointer = getPointerData(stage);
  if (!pointer) return;

  // Work in stage coordinates then normalize
  const clickStagePoint = { x: pointer.stage.x, y: pointer.stage.y };
  const points = extractShapePoints(room.shape);
  if (!points.length) return;
  const stagePts = points.map(toStagePoint);
  const insertAfter = findNearestSegmentIndex(stagePts, clickStagePoint);
  const normalized = toNormalizedPoint(clickStagePoint);
  const next = points.slice();
  next.splice(insertAfter + 1, 0, normalized);
  applyPointsToRoom(room, next);
};

const handleStageMouseLeave = () => {
  hideTooltip();
};

const handleRoomMouseDown = (event: KonvaEventObject<MouseEvent>) => {
  // In edit mode allow events to propagate so dblclick can be recognized
  if (!isEditingRoom.value) {
    event.cancelBubble = true;
  }
};

const handleStageWheel = (event: KonvaEventObject<WheelEvent>) => {
  event.evt.preventDefault();
  const stage = event.target.getStage();
  const pointer = getPointerData(stage);
  if (!pointer) {
    return;
  }
  const direction = event.evt.deltaY > 0 ? 1 / SCALE_STEP : SCALE_STEP;
  applyZoom(pointer.container, direction);
};

const handleStageDragMove = (event: KonvaEventObject<DragEvent>) => {
  stagePosition.x = event.target.x();
  stagePosition.y = event.target.y();
};

const handleStageDragEnd = (event: KonvaEventObject<DragEvent>) => {
  stagePosition.x = event.target.x();
  stagePosition.y = event.target.y();
};

watch(isDrawingRoom, (value) => {
  const stage = stageRef.value?.getStage();
  if (!stage) {
    return;
  }

  stage.container().style.cursor = value ? 'crosshair' : 'default';
  stage.draggable(!value && !isEditingRoom.value);

  if (!value) {
    drawingPoints.value = [];
  }
});

watch(isEditingRoom, (value) => {
  const stage = stageRef.value?.getStage();
  if (!stage) {
    return;
  }

  if (value) {
    stage.container().style.cursor = 'pointer';
  } else {
    stage.container().style.cursor = isDrawingRoom.value ? 'crosshair' : 'default';
  }
  stage.draggable(!isDrawingRoom.value && !value);
});

watch(stageScale, (value) => {
  const stage = stageRef.value?.getStage();
  if (!stage) {
    return;
  }
  stage.scale({ x: value, y: value });
  stage.batchDraw();
});

watch(
  () => ({ x: stagePosition.x, y: stagePosition.y }),
  (value) => {
    const stage = stageRef.value?.getStage();
    if (!stage) {
      return;
    }
    stage.position(value);
    stage.batchDraw();
  },
  { deep: true },
);

watch(
  selectedFloorId,
  () => {
    selectedRoomId.value = null;
    drawingPoints.value = [];
    isDrawingRoom.value = false;
    isEditingRoom.value = false;
    hideTooltip();
    closePopover();
    resetView();
  },
  { immediate: true },
);

watch(selectedRoomId, () => {
  if (!selectedRoomId.value) {
    isEditingRoom.value = false;
    closePopover();
  }
});

const tooltipRoomStatus = computed(() =>
  tooltip.room?.status ? ROOM_STATUS_LABELS[tooltip.room.status] : 'Помещение',
);

const popoverStatusLabel = computed(() => {
  if (!popover.room?.status) {
    return 'Помещение';
  }
  return ROOM_STATUS_LABELS[popover.room.status];
});

const legendPalette = (status: string) =>
  ROOM_STATUS_COLORS[status as keyof typeof ROOM_STATUS_COLORS] ?? ROOM_STATUS_COLORS.default;

const resolveAndClose = () => {
  modal.resolveModal({
    plan: clonePlan(editablePlan.value),
    selectedFloorId: selectedFloorId.value,
  });
};

const logCurrentFloorPlan = () => {
  const floor = activeFloor.value;
  // Log a deep-cloned plain object to avoid logging Vue proxies
  // and ensure the output is a complete snapshot
  console.log('Current floor plan:', floor ? JSON.parse(JSON.stringify(floor)) : null);
};
</script>

<template>
  <div class="modal-root">
    <div class="modal-header">
      <div>
        <h2 class="modal-title">{{ buildingTitle }}</h2>
        <p v-if="buildingPlan.address" class="modal-subtitle">
          {{ buildingPlan.address }}
        </p>
      </div>
      <button
        type="button"
        class="modal-close"
        aria-label="Закрыть"
        @click="modal.rejectModal('cancel')"
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>

    <div class="plan-toolbar">
      <template v-if="!isDrawingRoom">
        <button type="button" class="toolbar-button toolbar-button--primary" @click="startDrawing">
          Добавить помещение
        </button>
        <button
          type="button"
          class="toolbar-button"
          :disabled="!activeFloor"
          @click="logCurrentFloorPlan"
        >
          Вывести план этажа в консоль
        </button>
      </template>
      <template v-else>
        <div class="drawing-actions">
          <button
            type="button"
            class="toolbar-button toolbar-button--primary"
            :disabled="!isDrawingValid"
            @click="finishDrawing"
          >
            Сохранить зону
          </button>
          <button
            type="button"
            class="toolbar-button"
            :disabled="!drawingPoints.length"
            @click="undoDrawingPoint"
          >
            Шаг назад
          </button>
          <button type="button" class="toolbar-button" @click="cancelDrawing">
            Отменить
          </button>
        </div>
      </template>

      <div class="plan-toolbar__right">
        <button
          type="button"
          class="toolbar-button"
          :disabled="!selectedRoom"
          @click="toggleEditing"
        >
          {{ isEditingRoom ? 'Завершить редактирование' : 'Редактировать помещение' }}
        </button>
        <button
          type="button"
          class="toolbar-button toolbar-button--danger"
          :disabled="!selectedRoom"
          @click="deleteSelectedRoom"
        >
          Удалить
        </button>
      </div>
    </div>

    <div
      ref="planContainer"
      class="plan-container"
      :class="{ 'plan-container--editing': isEditingRoom, 'plan-container--drawing': isDrawingRoom }"
    >
      <v-stage
        v-if="stageSize.width && stageSize.height"
        ref="stageRef"
        class="plan-stage"
        :config="{
          width: stageSize.width,
          height: stageSize.height,
          scaleX: stageScale,
          scaleY: stageScale,
          x: stagePosition.x,
          y: stagePosition.y,
          draggable: !isDrawingRoom && !isEditingRoom,
        }"
        @click="handleStageClick"
        @dblclick="handleStageDoubleClick"
        @mouseleave="handleStageMouseLeave"
        @wheel="handleStageWheel"
        @dragmove="handleStageDragMove"
        @dragend="handleStageDragEnd"
      >
        <v-layer>
          <v-image
            v-if="backgroundImageConfig.visible && floorImageElement"
            :image="floorImageElement"
            :x="backgroundImageConfig.x"
            :y="backgroundImageConfig.y"
            :width="backgroundImageConfig.width"
            :height="backgroundImageConfig.height"
            :listening="false"
            :opacity="0.92"
          />

          <v-rect
            :x="0"
            :y="0"
            :width="stageSize.width"
            :height="stageSize.height"
            :cornerRadius="18"
            fill="rgba(241, 245, 249, 0.28)"
            stroke="rgba(148, 163, 184, 0.35)"
            :strokeWidth="1"
            :listening="false"
          />

          <v-line
            v-for="(line, index) in gridLines"
            :key="`grid-${index}`"
            :points="line.points"
            :dash="line.dash"
            :stroke="line.stroke"
            :strokeWidth="line.strokeWidth"
            :listening="false"
          />

          <v-group
            v-for="entry in scaledRooms"
            :key="entry.room.id"
            @mousedown="handleRoomMouseDown"
            @touchstart="handleRoomMouseDown"
            @dblclick="handlePolygonAddPoint(entry.room, $event)"
          >
            <v-line
              :points="entry.linePoints"
              :closed="true"
              :stroke="entry.room.id === selectedRoomId ? '#2563eb' : entry.palette.border"
              :strokeWidth="entry.room.id === selectedRoomId ? 4 : 2"
              :fill="entry.palette.bg"
              :opacity="hoveredRoomId === entry.room.id ? 0.95 : 0.82"
              :listening="!isDrawingRoom"
              strokeScaleEnabled="false"
              @mouseenter="handleRoomPointerEnter(entry.room, $event)"
              @mousemove="handleRoomPointerMove(entry.room, $event)"
              @mouseleave="handleRoomPointerLeave"
              @click="handleRoomClick(entry.room, $event)"
              @tap="handleRoomClick(entry.room, $event)"
              @dblclick="handlePolygonAddPoint(entry.room, $event)"
            />
            <v-text
              :text="entry.room.name"
              :x="entry.boundingStage.minX"
              :y="(entry.boundingStage.minY + entry.boundingStage.maxY) / 2 - 10"
              :width="entry.boundingStage.maxX - entry.boundingStage.minX"
              :align="'center'"
              :fontSize="labelFontSize"
              :fill="'#1f2937'"
              :listening="true"
              @dblclick="handlePolygonAddPoint(entry.room, $event)"
            />
          </v-group>

          <v-group v-if="isDrawingRoom && drawingPoints.length">
            <v-line
              :points="drawingLinePoints"
              :closed="drawingPoints.length >= 3"
              stroke="#2563eb"
              :strokeWidth="2"
              :dash="[8, 6]"
              :fill="drawingPoints.length >= 3 ? 'rgba(37, 99, 235, 0.18)' : 'transparent'"
              :listening="false"
            />
            <v-circle
              v-for="(point, index) in drawingStagePoints"
              :key="`draft-${index}`"
              :x="point.x"
              :y="point.y"
              :radius="6"
              fill="#2563eb"
              stroke="#fff"
              :strokeWidth="2"
              :listening="false"
            />
          </v-group>

          <v-group v-if="isEditingRoom && selectedRoomAnchors.length">
            <v-circle
              v-for="anchor in selectedRoomAnchors"
              :key="`anchor-${anchor.index}`"
              :x="anchor.stage.x"
              :y="anchor.stage.y"
              :radius="7"
              fill="#2563eb"
              stroke="#fff"
              :strokeWidth="2"
              :draggable="true"
              :dragBoundFunc="boundAnchorToStage"
              @mousedown="(e) => { e.cancelBubble = true }"
              @touchstart="(e) => { e.cancelBubble = true }"
              @click="(e) => { e.cancelBubble = true }"
              @tap="(e) => { e.cancelBubble = true }"
              @dragmove="handleAnchorDragMove(anchor.index, $event)"
              @dragstart="handleAnchorDragStart"
              @dragend="handleAnchorDragEnd"
            />
          </v-group>
        </v-layer>
      </v-stage>

      <div class="absolute top-2 left-2 flex flex-col flex-wrap gap-2 bg-[rgba(15,23,42,0.08)] backdrop-blur-[6px] p-3 rounded-[14px] border border-slate-200">
        <button
          v-for="floor in floors"
          :key="floor.id"
          type="button"
          :class="[
            'px-4 py-2.5 rounded-[10px] border text-[14px] font-medium transition-all duration-150 ease-in',
            floor.id === selectedFloorId
              ? 'border-blue-600 bg-[rgba(37,99,235,0.12)] text-blue-700 shadow-[0_0_0_1px_rgba(37,99,235,0.08)]'
              : 'border-transparent text-slate-600 bg-white hover:border-[rgba(59,130,246,0.4)] hover:text-blue-600'
          ]"
          @click="selectedFloorId = floor.id"
        >
          {{ floor.name }}
        </button>
      </div>

      <div class="absolute top-4 right-4 flex flex-col gap-2 bg-[rgba(15,23,42,0.08)] p-2 rounded-[14px] backdrop-blur-[6px] border border-[rgba(148,163,184,0.24)] shadow-[0_12px_28px_rgba(15,23,42,0.16)]">
        <button type="button" class="zoom-button" @click="zoomIn" aria-label="Увеличить">
          +
        </button>
        <button type="button" class="zoom-button" @click="zoomOut" aria-label="Уменьшить">
          -
        </button>
        <button type="button" class="zoom-button zoom-button--reset" @click="resetView" aria-label="Сбросить масштаб">
          R
        </button>
      </div>

      <div v-if="isDrawingRoom" class="drawing-hint">
        <p class="drawing-hint__title">Режим создания помещения</p>
        <p class="drawing-hint__text">
          Кликните на схеме, чтобы поставить точку. Минимум три точки. Двойной клик или кнопка
          «Сохранить» завершит построение.
        </p>
        <p class="drawing-hint__count">
          Точек: <strong>{{ drawingPoints.length }}</strong>
        </p>
      </div>

      <div
        v-if="tooltip.visible && tooltip.room"
        class="plan-tooltip"
        :style="tooltipStyle"
      >
        <p class="plan-tooltip__title">{{ tooltip.room.name }}</p>
        <p class="plan-tooltip__line">
          Статус: <span>{{ tooltipRoomStatus }}</span>
        </p>
        <p class="plan-tooltip__line" v-if="tooltip.room.tenant">
          Арендатор: <span>{{ tooltip.room.tenant }}</span>
        </p>
        <p class="plan-tooltip__line" v-if="tooltip.room.area">
          Площадь: <span>{{ tooltip.room.area }} м²</span>
        </p>
      </div>

      <div
        v-if="popover.visible && popover.room"
        class="plan-popover"
        :style="popoverStyle"
        @mousedown.stop
        @mouseup.stop
      >
        <button type="button" class="plan-popover__close" @click="closePopover">×</button>
        <h3 class="plan-popover__title">{{ popover.room.name }}</h3>
        <p class="plan-popover__status">
          {{ popoverStatusLabel }}
          <span v-if="popover.room.area">· {{ popover.room.area }} м²</span>
        </p>
        <div class="plan-popover__form">
          <label class="plan-popover__label">
            Название
            <input v-model="popover.room.name" type="text" class="plan-popover__input" />
          </label>
          <label class="plan-popover__label">
            Статус
            <select v-model="popoverStatusModel" class="plan-popover__input">
              <option value="">Не указан</option>
              <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="plan-popover__label">
            Арендатор
            <input
              v-model="popover.room.tenant"
              type="text"
              class="plan-popover__input"
              placeholder="Не указано"
            />
          </label>
          <label class="plan-popover__label">
            Описание
            <textarea
              v-model="popover.room.description"
              rows="3"
              class="plan-popover__textarea"
              placeholder="Комментарий по помещению"
            />
          </label>
          <label class="plan-popover__label">
            Площадь, м²
            <input
              v-model.number="popover.room.area"
              type="number"
              min="0"
              step="1"
              class="plan-popover__input"
            />
          </label>
        </div>
      </div>
    </div>

    <div class="plan-meta">
      <div class="plan-meta__item" v-if="activeFloor?.note">
        <h4>Комментарий</h4>
        <p>{{ activeFloor.note }}</p>
      </div>
      <div class="plan-meta__item" v-if="totalFloorArea">
        <h4>Общая площадь этажа</h4>
        <p>{{ totalFloorArea }} м²</p>
      </div>
      <div class="plan-meta__legend">
        <h4>Статусы помещений</h4>
        <div class="legend">
          <div v-for="item in legendItems" :key="item.status" class="legend__item">
            <span
              class="legend__color"
              :style="{
                background: legendPalette(item.status).bg,
                borderColor: legendPalette(item.status).border,
              }"
            />
            <span>{{ item.label }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button type="button" class="footer-button" @click="modal.rejectModal('cancel')">
        Отменить
      </button>
      <button type="button" class="footer-button footer-button--primary" @click="resolveAndClose">
        Сохранить план
      </button>
    </div>
  </div>
</template>

<style scoped>
.modal-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  border-radius: 16px;
  background: #ffffff;
  padding: 32px;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.12);
  gap: 20px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
}

.modal-title {
  font-size: 22px;
  font-weight: 600;
  color: #111827;
}

.modal-subtitle {
  margin-top: 4px;
  font-size: 14px;
  color: #6b7280;
}

.modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  background: rgba(15, 23, 42, 0.05);
  color: #1f2937;
  font-size: 22px;
  line-height: 1;
  transition: background 0.12s ease, color 0.12s ease;
}

.modal-close:hover {
  background: rgba(15, 23, 42, 0.12);
  color: #111827;
}

.floor-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  background: #f8fafc;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
}

.floor-button {
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  background: #ffffff;
  transition: all 0.16s ease;
}

.floor-button:hover {
  border-color: rgba(59, 130, 246, 0.4);
  color: #2563eb;
}

.floor-button--active {
  border-color: #2563eb;
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
  box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.08);
}

.plan-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.plan-toolbar__right {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.toolbar-button {
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid #cbd5f5;
  background: #ffffff;
  color: #1f2937;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.16s ease;
}

.toolbar-button:hover:enabled {
  border-color: #2563eb;
  color: #2563eb;
}

.toolbar-button:disabled {
  color: #94a3b8;
  border-color: #e2e8f0;
  cursor: not-allowed;
}

.toolbar-button--primary {
  border-color: transparent;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #ffffff;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.22);
}

.toolbar-button--primary:hover:enabled {
  background: linear-gradient(135deg, #1d4ed8, #1e40af);
}

.toolbar-button--danger {
  border-color: rgba(244, 63, 94, 0.35);
  color: #f43f5e;
  background: rgba(254, 226, 226, 0.35);
}

.toolbar-button--danger:hover:enabled {
  border-color: #f43f5e;
  background: rgba(254, 202, 202, 0.55);
}

.drawing-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.plan-container {
  position: relative;
  flex: 1;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 12px;
}

.plan-container--drawing {
  border-color: rgba(37, 99, 235, 0.35);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
}

.plan-container--editing {
  border-color: rgba(20, 184, 166, 0.35);
  box-shadow: 0 0 0 2px rgba(20, 184, 166, 0.1);
}

.plan-stage {
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
}

.zoom-controls {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: rgba(15, 23, 42, 0.08);
  padding: 8px;
  border-radius: 14px;
  backdrop-filter: blur(6px);
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
}

.zoom-button {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  background: rgba(248, 250, 252, 0.9);
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s ease;
}

.zoom-button:hover {
  border-color: rgba(37, 99, 235, 0.6);
  color: #1d4ed8;
  background: rgba(37, 99, 235, 0.12);
}

.zoom-button--reset {
  font-size: 16px;
}

.drawing-hint {
  position: absolute;
  right: 18px;
  top: 18px;
  width: 260px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
  font-size: 13px;
  backdrop-filter: blur(6px);
  border: 1px solid rgba(37, 99, 235, 0.18);
}

.drawing-hint__title {
  font-weight: 600;
  margin-bottom: 4px;
}

.drawing-hint__text {
  color: #1e3a8a;
  margin-bottom: 6px;
  line-height: 1.4;
}

.drawing-hint__count {
  color: #1e3a8a;
}

.plan-tooltip {
  position: absolute;
  min-width: 180px;
  max-width: 220px;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.9);
  color: #f8fafc;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.3;
  pointer-events: none;
  transform: translate(-50%, calc(-100% - 12px));
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.18);
}

.plan-tooltip__title {
  font-weight: 600;
  margin-bottom: 4px;
}

.plan-tooltip__line {
  color: rgba(248, 250, 252, 0.8);
  margin-top: 2px;
}

.plan-tooltip__line span {
  color: #f8fafc;
  font-weight: 500;
}

.plan-popover {
  position: absolute;
  width: 280px;
  padding: 16px 18px 18px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
  transform: translate(-50%, calc(-100% - 18px));
  pointer-events: auto;
}

.plan-popover__close {
  position: absolute;
  top: 8px;
  right: 10px;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  font-size: 18px;
  background: rgba(15, 23, 42, 0.08);
  color: #1f2937;
  line-height: 1;
}

.plan-popover__close:hover {
  background: rgba(15, 23, 42, 0.16);
}

.plan-popover__title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
  padding-right: 26px;
}

.plan-popover__status {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 12px;
}

.plan-popover__form {
  display: grid;
  gap: 10px;
}

.plan-popover__label {
  display: grid;
  gap: 4px;
  font-size: 12px;
  color: #475569;
}

.plan-popover__input,
.plan-popover__textarea,
.plan-popover__select {
  width: 100%;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #dbe3f2;
  background: #f8fafc;
  font-size: 13px;
  color: #1f2937;
  transition: border 0.12s ease, box-shadow 0.12s ease;
}

.plan-popover__input:focus,
.plan-popover__textarea:focus,
.plan-popover__select:focus {
  border-color: #2563eb;
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
  outline: none;
}

.plan-popover__textarea {
  resize: vertical;
  min-height: 72px;
}

.plan-meta {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  font-size: 13px;
  color: #475569;
}

.plan-meta__item {
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 12px 14px;
}

.plan-meta__item h4 {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 6px;
}

.plan-meta__legend {
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 12px 14px;
}

.plan-meta__legend h4 {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.legend__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 12px;
  color: #475569;
}

.legend__color {
  display: inline-flex;
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 2px solid transparent;
}

.modal-footer {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  gap: 14px;
}

.footer-button {
  padding: 12px 22px;
  border-radius: 12px;
  border: 1px solid #dbe3f2;
  background: #ffffff;
  color: #1f2937;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.16s ease;
}

.footer-button:hover {
  border-color: #1f2937;
  color: #0f172a;
}

.footer-button--primary {
  border-color: transparent;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #ffffff;
  box-shadow: 0 12px 28px rgba(37, 99, 235, 0.28);
}

.footer-button--primary:hover {
  background: linear-gradient(135deg, #1d4ed8, #1e3a8a);
}

@media (max-width: 1024px) {
  .modal-root {
    padding: 24px;
  }

  .plan-meta {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .modal-root {
    padding: 20px;
  }

  .modal-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .footer-button {
    width: 100%;
  }
}
</style>
